/**
 * EligibilityFormBuilder Component
 *
 * Visual form builder for eligibility configuration.
 * Supports nested conditions (AND, OR, NOT) and comparison conditions.
 */

import type { EligibilityConfig, EligibilityRule, ComparisonOperator } from "@tatvex/config";
import { Filter, Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { useCallback, useState } from "react";

import type {
  EligibilityFormBuilderProps,
  ConditionValue,
  ComparisonConditionValue,
  FieldError,
} from "./types";
import {
  createEmptyEligibilityRule,
  createEmptyComparisonCondition,
  isComparisonCondition,
  isAndCondition,
  isOrCondition,
  isNotCondition,
  generateId,
  isValidScreamingSnakeCase,
} from "./types";

// ============================================================================
// Operator Options
// ============================================================================

const COMPARISON_OPERATORS: readonly { value: ComparisonOperator; label: string }[] = [
  { value: "eq", label: "Equals (=)" },
  { value: "neq", label: "Not Equals (!=)" },
  { value: "gt", label: "Greater Than (>)" },
  { value: "gte", label: "Greater or Equal (>=)" },
  { value: "lt", label: "Less Than (<)" },
  { value: "lte", label: "Less or Equal (<=)" },
  { value: "in", label: "In List" },
  { value: "not_in", label: "Not In List" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does Not Contain" },
  { value: "starts_with", label: "Starts With" },
  { value: "ends_with", label: "Ends With" },
  { value: "between", label: "Between" },
  { value: "is_null", label: "Is Null" },
  { value: "is_not_null", label: "Is Not Null" },
  { value: "regex", label: "Regex Match" },
  { value: "exists", label: "Exists" },
];

// ============================================================================
// Condition Builder Sub-Component
// ============================================================================

interface ConditionBuilderProps {
  readonly value: ConditionValue;
  readonly onChange: (value: ConditionValue) => void;
  readonly onDelete?: (() => void) | undefined;
  readonly availableFields?: readonly string[];
  readonly depth?: number;
  readonly readOnly?: boolean;
}

function ConditionBuilder({
  value,
  onChange,
  onDelete,
  availableFields = [],
  depth = 0,
  readOnly = false,
}: ConditionBuilderProps): React.JSX.Element {
  // Determine condition type
  const isComparison = isComparisonCondition(value);
  const isAnd = isAndCondition(value);
  const isOr = isOrCondition(value);
  const isNot = isNotCondition(value);

  const handleTypeChange = useCallback(
    (newType: string) => {
      switch (newType) {
        case "comparison":
          onChange(createEmptyComparisonCondition());
          break;
        case "and":
          onChange({ type: "and", conditions: [createEmptyComparisonCondition()] });
          break;
        case "or":
          onChange({ type: "or", conditions: [createEmptyComparisonCondition()] });
          break;
        case "not":
          onChange({ type: "not", condition: createEmptyComparisonCondition() });
          break;
      }
    },
    [onChange]
  );

  const getCurrentType = (): string => {
    if (isAnd) return "and";
    if (isOr) return "or";
    if (isNot) return "not";
    return "comparison";
  };

  // Render comparison condition
  if (isComparison) {
    const comp = value as ComparisonConditionValue;
    return (
      <div className="condition-comparison flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2">
        {/* Condition type selector */}
        {depth > 0 && (
          <select
            value="comparison"
            onChange={(e) => handleTypeChange(e.target.value)}
            disabled={readOnly}
            className="w-24 rounded border border-gray-300 px-2 py-1 text-xs"
          >
            <option value="comparison">Compare</option>
            <option value="and">AND</option>
            <option value="or">OR</option>
            <option value="not">NOT</option>
          </select>
        )}

        {/* Field */}
        {availableFields.length > 0 ? (
          <select
            value={comp.field}
            onChange={(e) => onChange({ ...comp, field: e.target.value })}
            disabled={readOnly}
            className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">Select field...</option>
            {availableFields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={comp.field}
            onChange={(e) => onChange({ ...comp, field: e.target.value })}
            readOnly={readOnly}
            placeholder="field.path"
            className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
          />
        )}

        {/* Operator */}
        <select
          value={comp.operator}
          onChange={(e) => onChange({ ...comp, operator: e.target.value as ComparisonOperator })}
          disabled={readOnly}
          className="w-36 rounded border border-gray-300 px-2 py-1 text-sm"
        >
          {COMPARISON_OPERATORS.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>

        {/* Value / ValueRef */}
        {!["is_null", "is_not_null", "exists"].includes(comp.operator) && (
          <>
            <input
              type="text"
              value={String(comp.value ?? "")}
              onChange={(e) => {
                const val = e.target.value;
                // Try to parse as number or boolean
                let parsed: unknown = val;
                if (val === "true") parsed = true;
                else if (val === "false") parsed = false;
                else if (!isNaN(Number(val)) && val !== "") parsed = Number(val);
                // Remove valueRef when setting value
                const { valueRef: _, ...rest } = comp;
                void _;
                onChange({ ...rest, value: parsed } as ComparisonConditionValue);
              }}
              readOnly={readOnly}
              placeholder="Value"
              className="w-28 rounded border border-gray-300 px-2 py-1 text-sm"
            />
            <span className="text-xs text-gray-400">or</span>
            <input
              type="text"
              value={comp.valueRef ?? ""}
              onChange={(e) => {
                const newValueRef = e.target.value || undefined;
                if (newValueRef) {
                  // Remove value when setting valueRef
                  const { value: _, ...rest } = comp;
                  void _;
                  onChange({ ...rest, valueRef: newValueRef } as ComparisonConditionValue);
                } else {
                  // Remove valueRef when clearing it
                  const { valueRef: _, ...rest } = comp;
                  void _;
                  onChange(rest as ComparisonConditionValue);
                }
              }}
              readOnly={readOnly}
              placeholder="user.input"
              className="w-28 rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </>
        )}

        {/* Delete */}
        {!readOnly && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1 text-gray-400 hover:text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // Render logical condition (AND/OR/NOT)
  const bgColor = depth % 2 === 0 ? "bg-gray-50" : "bg-gray-100";
  const borderColor = isAnd ? "border-blue-200" : isOr ? "border-green-200" : "border-yellow-200";
  const labelBg = isAnd
    ? "bg-blue-100 text-blue-800"
    : isOr
      ? "bg-green-100 text-green-800"
      : "bg-yellow-100 text-yellow-800";

  return (
    <div className={`condition-group rounded-md border ${borderColor} ${bgColor} p-3`}>
      <div className="mb-2 flex items-center gap-2">
        {/* Type selector */}
        <select
          value={getCurrentType()}
          onChange={(e) => handleTypeChange(e.target.value)}
          disabled={readOnly}
          className={`rounded px-2 py-1 text-xs font-medium ${labelBg}`}
        >
          <option value="comparison">Compare</option>
          <option value="and">AND</option>
          <option value="or">OR</option>
          <option value="not">NOT</option>
        </select>

        {(isAnd || isOr) && (
          <span className="text-xs text-gray-500">
            {isAnd ? "All conditions must match" : "Any condition must match"}
          </span>
        )}

        {isNot && <span className="text-xs text-gray-500">Condition must NOT match</span>}

        {!readOnly && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto rounded p-1 text-gray-400 hover:text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Nested conditions */}
      <div className="space-y-2">
        {(isAnd || isOr) && (
          <>
            {(value as unknown as { conditions: readonly ConditionValue[] }).conditions.map(
              (cond, idx) => (
                <ConditionBuilder
                  key={idx}
                  value={cond}
                  onChange={(newCond) => {
                    const conditions = [
                      ...(value as unknown as { conditions: readonly ConditionValue[] }).conditions,
                    ];
                    conditions[idx] = newCond;
                    onChange({ ...value, conditions } as ConditionValue);
                  }}
                  onDelete={
                    (value as unknown as { conditions: readonly ConditionValue[] }).conditions
                      .length > 1
                      ? () => {
                          const conditions = (
                            value as unknown as { conditions: readonly ConditionValue[] }
                          ).conditions.filter((_, i) => i !== idx);
                          onChange({ ...value, conditions } as ConditionValue);
                        }
                      : undefined
                  }
                  availableFields={availableFields}
                  depth={depth + 1}
                  readOnly={readOnly}
                />
              )
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  const conditions = [
                    ...(value as unknown as { conditions: readonly ConditionValue[] }).conditions,
                    createEmptyComparisonCondition(),
                  ];
                  onChange({ ...value, conditions } as ConditionValue);
                }}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <Plus className="h-4 w-4" />
                Add Condition
              </button>
            )}
          </>
        )}

        {isNot && (
          <ConditionBuilder
            value={(value as { condition: ConditionValue }).condition}
            onChange={(newCond) => onChange({ type: "not", condition: newCond })}
            availableFields={availableFields}
            depth={depth + 1}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Rule Card Sub-Component
// ============================================================================

interface RuleCardProps {
  readonly value: EligibilityRule;
  readonly onChange: (value: EligibilityRule) => void;
  readonly onDelete: () => void;
  readonly availableFields?: readonly string[];
  readonly readOnly?: boolean;
}

function RuleCard({
  value,
  onChange,
  onDelete,
  availableFields = [],
  readOnly = false,
}: RuleCardProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleFieldChange = useCallback(
    <K extends keyof EligibilityRule>(key: K, newValue: EligibilityRule[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  const handleConditionChange = useCallback(
    (index: number, newCondition: ConditionValue) => {
      const conditions = [...value.conditions] as unknown[];
      conditions[index] = newCondition;
      onChange({ ...value, conditions } as EligibilityRule);
    },
    [value, onChange]
  );

  const handleAddCondition = useCallback(() => {
    const conditions = [...value.conditions, createEmptyComparisonCondition()] as unknown[];
    onChange({ ...value, conditions } as EligibilityRule);
  }, [value, onChange]);

  const handleDeleteCondition = useCallback(
    (index: number) => {
      const conditions = value.conditions.filter((_, i) => i !== index);
      onChange({ ...value, conditions });
    },
    [value, onChange]
  );

  const reasonCodeValid = !value.reasonCode || isValidScreamingSnakeCase(value.reasonCode);

  return (
    <div className="rule-card rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between border-b border-gray-100 px-4 py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-purple-500" />
          <div>
            <h4 className="font-medium text-gray-900">{value.name || "Unnamed Rule"}</h4>
            <p className="text-sm text-gray-500">
              {value.conditions.length} condition{value.conditions.length !== 1 ? "s" : ""}
              {value.reasonCode && (
                <span className="ml-2 font-mono text-xs">[{value.reasonCode}]</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!readOnly && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="space-y-4 p-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">ID</label>
              <input
                type="text"
                value={value.id}
                onChange={(e) => handleFieldChange("id", e.target.value)}
                readOnly={readOnly}
                placeholder="rule_id"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={value.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                readOnly={readOnly}
                placeholder="Rule Name"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              value={value.description ?? ""}
              onChange={(e) => handleFieldChange("description", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Optional description..."
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          {/* Conditions */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Conditions (all must match)
              </label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleAddCondition}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-blue-600 hover:bg-blue-50"
                >
                  <Plus className="h-4 w-4" />
                  Add Condition
                </button>
              )}
            </div>

            <div className="space-y-2">
              {value.conditions.map((condition, index) => (
                <ConditionBuilder
                  key={index}
                  value={condition as ConditionValue}
                  onChange={(c) => handleConditionChange(index, c)}
                  onDelete={
                    value.conditions.length > 1 ? () => handleDeleteCondition(index) : undefined
                  }
                  availableFields={availableFields}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>

          {/* Exclusion Reason */}
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3">
            <h5 className="mb-2 flex items-center gap-2 text-sm font-medium text-yellow-800">
              <AlertTriangle className="h-4 w-4" />
              Exclusion Reason
            </h5>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">
                  Reason Code (SCREAMING_SNAKE_CASE)
                </label>
                <input
                  type="text"
                  value={value.reasonCode}
                  onChange={(e) => handleFieldChange("reasonCode", e.target.value.toUpperCase())}
                  readOnly={readOnly}
                  placeholder="RULE_NOT_MET"
                  className={`rounded-md border px-3 py-2 font-mono text-sm ${
                    reasonCodeValid ? "border-gray-300" : "border-red-300"
                  }`}
                />
                {!reasonCodeValid && (
                  <p className="text-xs text-red-500">Must be SCREAMING_SNAKE_CASE</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Priority</label>
                <input
                  type="number"
                  value={value.priority ?? ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "priority",
                      e.target.value ? parseInt(e.target.value, 10) : undefined
                    )
                  }
                  readOnly={readOnly}
                  min={0}
                  placeholder="0"
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-600">Reason Message</label>
              <input
                type="text"
                value={value.reasonMessage}
                onChange={(e) => handleFieldChange("reasonMessage", e.target.value)}
                readOnly={readOnly}
                placeholder="Product not available due to..."
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * EligibilityFormBuilder - Visual eligibility configuration
 *
 * CSS Classes:
 * - eligibility-form: Container element
 * - eligibility-form__header: Header with title and add button
 * - eligibility-form__rules: Rule cards container
 */
export function EligibilityFormBuilder({
  value,
  onChange,
  onValidate,
  readOnly = false,
  errors = [],
  className = "",
  availableFields = [],
}: EligibilityFormBuilderProps): React.JSX.Element {
  const [localErrors, setLocalErrors] = useState<readonly FieldError[]>([]);
  // Merge errors from external and local validation
  void [...errors, ...localErrors];

  const handleChange = useCallback(
    <K extends keyof EligibilityConfig>(key: K, newValue: EligibilityConfig[K]) => {
      const updated: EligibilityConfig = {
        ...value,
        [key]: newValue,
      };

      if (onValidate) {
        const result = onValidate(updated);
        setLocalErrors(result.errors);
      }

      onChange(updated);
    },
    [value, onChange, onValidate]
  );

  const handleRuleChange = useCallback(
    (index: number, newRule: EligibilityRule) => {
      const newRules = [...value.rules];
      newRules[index] = newRule;
      handleChange("rules", newRules);
    },
    [value.rules, handleChange]
  );

  const handleAddRule = useCallback(() => {
    const newRule = {
      ...createEmptyEligibilityRule(),
      id: generateId("rule"),
    } as EligibilityRule;
    handleChange("rules", [...value.rules, newRule]);
  }, [value.rules, handleChange]);

  const handleDeleteRule = useCallback(
    (index: number) => {
      handleChange(
        "rules",
        value.rules.filter((_, i) => i !== index)
      );
    },
    [value.rules, handleChange]
  );

  return (
    <div className={`eligibility-form flex flex-col gap-6 ${className}`}>
      {/* Header Info */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Vertical Key</label>
            <input
              type="text"
              value={value.verticalKey}
              onChange={(e) => handleChange("verticalKey", e.target.value)}
              readOnly={readOnly}
              placeholder="vertical-key"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Version</label>
            <input
              type="number"
              value={value.version}
              onChange={(e) => handleChange("version", parseInt(e.target.value, 10) || 1)}
              readOnly={readOnly}
              min={1}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Default Exclusion Reason</label>
            <input
              type="text"
              value={value.defaultExclusionReason ?? ""}
              onChange={(e) => handleChange("defaultExclusionReason", e.target.value || undefined)}
              readOnly={readOnly}
              placeholder="Product not available"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div>
        <div className="eligibility-form__header mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Eligibility Rules</h3>
          {!readOnly && (
            <button
              type="button"
              onClick={handleAddRule}
              className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              <Plus className="h-4 w-4" />
              Add Rule
            </button>
          )}
        </div>

        <div className="eligibility-form__rules space-y-4">
          {value.rules.map((rule, index) => (
            <RuleCard
              key={rule.id || index}
              value={rule}
              onChange={(r) => handleRuleChange(index, r)}
              onDelete={() => handleDeleteRule(index)}
              availableFields={availableFields as string[]}
              readOnly={readOnly}
            />
          ))}
        </div>

        {value.rules.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 py-8 text-center">
            <Filter className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No eligibility rules defined.</p>
            {!readOnly && (
              <button
                type="button"
                onClick={handleAddRule}
                className="mt-4 inline-flex items-center gap-1 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                <Plus className="h-4 w-4" />
                Add First Rule
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export type { EligibilityFormBuilderProps };
